'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import { useEffect, useState } from 'react';

import cx from 'classnames';
import { AnimatePresence, m } from 'framer-motion';

import Text from 'components/ui/Text';

import styles from './LocationStatus.module.scss';

const motionVariants = {
    initial: {
        opacity: 0,
        y: 20,
    },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.25,
            delay: 0.5,
        },
    },
    exit: {
        opacity: 0,
        y: -20,
        transition: {
            duration: 0.25,
            delay: 0,
        },
    },
};

// Helper function to convert time string (HH:MM) to minutes for easier comparison
const convertTimeToMinutes = timeStr => {
    // Make sure we have a valid time string
    if (!timeStr || typeof timeStr !== 'string') return 0;

    const [hours, minutes] = timeStr.split(':').map(Number);
    return (isNaN(hours) ? 0 : hours) * 60 + (isNaN(minutes) ? 0 : minutes);
};

// Render Open, Closed, or Temporary Closed based on location.hours object and current time in to the timezone of the location
const LocationStatus = ({ location }) => {
    const { hours, timezone = 'Pacific Time', temporaryClosed } = location;
    const [currentTime, setCurrentTime] = useState(null);
    const [isClient, setIsClient] = useState(false);

    // Ensure we're on the client side and get current time
    useEffect(() => {
        setIsClient(true);
        setCurrentTime(new Date());

        // Update time every minute to keep status current
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, []);

    // Check if location is temporarily closed
    if (temporaryClosed) {
        return (
            <Text as="span" baseTheme="labelLarge">
                <m.span
                    {...motionVariants}
                    className={cx(styles.closed, styles.pill)}
                >
                    Temporary Closed
                </m.span>
            </Text>
        );
    }

    // Show loading state until client-side hydration is complete
    if (!isClient || !currentTime) {
        return (
            <Text
                as="span"
                baseTheme="labelLarge"
                className={cx(styles.pill, styles.loading)}
            >
                <span className={styles.closed}>Loading...</span>
            </Text>
        );
    }

    // Get current date and time in the timezone of the location
    const now = currentTime;

    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
    ];

    // Map display timezone to IANA timezone identifier
    const getTimezoneIdentifier = displayTimezone => {
        const timezoneMap = {
            'Eastern Time': 'America/New_York',
            'Central Time': 'America/Chicago',
            'Mountain Time': 'America/Denver',
            'Pacific Time': 'America/Los_Angeles',
            'Alaska Time': 'America/Anchorage',
            'Hawaii–Aleutian Time': 'Pacific/Honolulu',
        };
        return timezoneMap[displayTimezone] || 'America/Los_Angeles'; // Default to Pacific Time if not found
    };

    // Convert to location's timezone
    const locationTimezone = getTimezoneIdentifier(timezone);
    const localDate = new Date(
        now.toLocaleString('en-US', { timeZone: locationTimezone })
    );
    const localDay = dayNames[localDate.getDay()];

    // Get current time in minutes (hours * 60 + minutes)
    const currentTimeMinutes =
        localDate.getHours() * 60 + localDate.getMinutes();

    // Check if the location is open today
    const todayHours = hours[localDay];
    if (!todayHours || !todayHours.isOpen || todayHours.periods.length === 0) {
        return (
            <Text as="span" baseTheme="labelLarge" className={styles.pill}>
                <span className={styles.closed}>Temporarily Closed</span>
            </Text>
        );
    }

    // Check if current time falls within any open period
    const isCurrentlyOpen = todayHours.periods.some(period => {
        const openMinutes = convertTimeToMinutes(period.open);
        const closeMinutes = convertTimeToMinutes(period.close);
        return (
            currentTimeMinutes >= openMinutes &&
            currentTimeMinutes < closeMinutes
        );
    });

    // Helper function to find next opening time
    const findNextOpening = () => {
        // First, check if there's another opening period today
        const nextTodayOpening = todayHours.periods.find(period => {
            const openMinutes = convertTimeToMinutes(period.open);
            return openMinutes > currentTimeMinutes;
        });

        if (nextTodayOpening) {
            return {
                time: nextTodayOpening.open,
                isToday: true,
            };
        }

        // If no more openings today, check the next 7 days
        for (let i = 1; i <= 7; i++) {
            const nextDayIndex = (localDate.getDay() + i) % 7;
            const nextDayName = dayNames[nextDayIndex];
            const nextDayHours = hours[nextDayName];

            if (
                nextDayHours &&
                nextDayHours.isOpen &&
                nextDayHours.periods.length > 0
            ) {
                const firstPeriod = nextDayHours.periods[0];
                return {
                    time: firstPeriod.open,
                    isToday: false,
                    dayName: nextDayName,
                    daysFromNow: i,
                };
            }
        }

        return null; // No opening found in the next week
    };

    // Helper function to format time from 24-hour to 12-hour format
    const formatTime = timeStr => {
        if (!timeStr) return '';

        const [hours, minutes] = timeStr.split(':').map(Number);
        const period = hours >= 12 ? 'pm' : 'am';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

        // Only show minutes if they're not 00
        if (minutes === 0) {
            return `${displayHours}${period}`;
        }
        return `${displayHours}:${minutes
            .toString()
            .padStart(2, '0')}${period}`;
    };

    const nextOpening = findNextOpening();
    const formattedTime = formatTime(nextOpening.time);

    return (
        <AnimatePresence>
            <Text as="span" baseTheme="labelLarge">
                {isCurrentlyOpen && (
                    <m.span
                        {...motionVariants}
                        key="open"
                        className={cx(styles.open, styles.pill)}
                    >
                        Open Now
                    </m.span>
                )}

                {!isCurrentlyOpen && !nextOpening && (
                    <m.span
                        {...motionVariants}
                        key="open-today"
                        className={cx(styles.closed, styles.pill)}
                    >
                        Opens at {formatTime(nextOpening.time)}
                    </m.span>
                )}

                {!isCurrentlyOpen && nextOpening && nextOpening.isToday && (
                    <m.span
                        {...motionVariants}
                        key="open-today"
                        className={cx(styles.closed, styles.pill)}
                    >
                        Opens at {formatTime(nextOpening.time)}
                    </m.span>
                )}

                {!isCurrentlyOpen &&
                    nextOpening &&
                    nextOpening.daysFromNow === 1 && (
                        <m.span
                            {...motionVariants}
                            key="open-next-day"
                            className={cx(styles.closed, styles.pill)}
                        >
                            Opens tomorrow at {formattedTime}
                        </m.span>
                    )}

                {!isCurrentlyOpen &&
                    nextOpening &&
                    nextOpening.daysFromNow > 1 && (
                        <m.span
                            {...motionVariants}
                            key="open-next-day"
                            className={cx(styles.closed, styles.pill)}
                        >
                            Opens {nextOpening.dayName} at {formattedTime}
                        </m.span>
                    )}
            </Text>
        </AnimatePresence>
    );
};

export default LocationStatus;
